import { createFormStore } from "./create-form-store";

/**
 * One store per surface that can render a form.
 *
 * They are declared here, beside the wizard, rather than in the apps that mount them: everything a
 * form store holds is a forms concept, and the wizard itself needs to read the store, which it could
 * not do if the declaration lived in an app. Hosts import the store for their surface directly.
 *
 * They are separate stores on purpose. Two of these surfaces can be open at once, so a single shared
 * store would let one form overwrite another's answers, and each surface persists differently.
 */

/** Form opened from a response in the portal's document list. In-memory: no link carries its state. */
const formResponseDialogStore = createFormStore({ name: "form-response-dialog" });
export const { useStore: useFormResponseDialogStore, useShallowStore: useFormResponseDialogShallowStore } =
  formResponseDialogStore;

/** Pinned form opened from the portal home. Separate from the response dialog so both can be open. */
const pinnedFormDialogStore = createFormStore({ name: "pinned-form-dialog" });
export const { useStore: usePinnedFormDialogStore, useShallowStore: usePinnedFormDialogShallowStore } =
  pinnedFormDialogStore;

/**
 * The `/link/form` deeplink. Persists to the query string, because this is the one surface whose
 * state should survive a copied or reopened link.
 */
const formLinkStore = createFormStore({
  name: "form-link",
  persist: "urlParams",
  // Bare rather than namespaced: `?logic` is the param existing links already carry, so the store
  // reads and writes the shape the links define rather than imposing its own.
  urlParamKeys: ["logic"],
});
export const { useStore: useFormLinkStore, useShallowStore: useFormLinkShallowStore } = formLinkStore;

/** The single-page (non-wizard) form. In-memory, like the in-app dialogs. */
const singlePageFormStore = createFormStore({ name: "single-page-form" });
export const { useStore: useSinglePageFormStore, useShallowStore: useSinglePageFormShallowStore } = singlePageFormStore;

/**
 * Resets every form store.
 *
 * These live at module scope, so within a test file they persist between cases the way they persist
 * between forms in the app — one test's navigation position would otherwise become the next one's
 * starting state. Call it from a global `beforeEach`; application code resets the single store it
 * owns instead, from the event that begins a form.
 */
export const resetAllFormStores = () => {
  [formResponseDialogStore, pinnedFormDialogStore, formLinkStore, singlePageFormStore].forEach((store) =>
    store.useStore.getState().reset()
  );
};

/**
 * Maps each wizard surface to the store it reads. Type-only: the wizard cannot select from it at
 * runtime, since choosing a hook per render would mean calling hooks conditionally — each surface has
 * its own component that calls its hook directly. Declaring it this way still ties `FormSurface` to
 * the stores above, so a surface cannot be named without a store to back it.
 */
type FormStoresBySurface = {
  "form-response-dialog": typeof useFormResponseDialogStore;
  "pinned-form-dialog": typeof usePinnedFormDialogStore;
  "form-link": typeof useFormLinkStore;
};

export type FormSurface = keyof FormStoresBySurface;
